import '@angular/compiler';
import {
  Component,
  ComponentRef,
  Inject,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ShopService } from '@app/services/shops/shop.service';
import { UserService } from '@app/services/user/user.service';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { Rule } from 'src/app/sharedModule/models/rule.model';
import {
  Step,
  findActionByNameAndValue,
  findConditionByNameAndValue,
} from '../../../../../features/automation-rules/utils';
import { AutomationRulesActions } from '../../../actions';
import { AutomationRulesReducer } from '../../../reducers';
import { RuleItem, RulesListComponent } from '../../rules-list/rules-list.component';

interface DialogData {
  step: Step;
}

@Component({
  selector: 'automation-rules-list-dialog',
  templateUrl: './automation-rules-list-dialog.html',
  styleUrls: ['./automation-rules-list-dialog.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AutomationRulesListDialog implements OnInit, OnDestroy {
  componentRef: ComponentRef<any>;

  activeRulesCount: number = 0;
  step: Step;
  isLoading: boolean = false;
  isLoading$ = this.store.select(AutomationRulesReducer.selectIsLoading);
  isLoadingSubscription$: Subscription;
  templateData = null;
  templateData$ = this.store.select(AutomationRulesReducer.selectTemplateData);
  templateDataSubscription$: Subscription;
  rulesData$ = this.store.select(AutomationRulesReducer.selectRulesData);
  rulesDataSubscription$: Subscription;
  disableAddRule;
  isAdminUser;

  displayedMenuItems = [
    {
      text: Step.verify,
    },
    {
      text: Step.assign,
    },
    {
      text: Step.fulfill,
    },
    {
      text: Step.track,
    },
  ];

  @ViewChild('target', { read: ViewContainerRef, static: true })
  vcRef: ViewContainerRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialogRef: MatDialogRef<AutomationRulesListDialog>,
    private store: Store,
    private _shopService: ShopService,
    private userService: UserService,
  ) {}

  ngOnInit() {
    this.initDialogData();
    this.createComponent();

    this.componentRef.instance.onChangeRulePriority.subscribe((event) =>
      this.store.dispatch(AutomationRulesActions.changeRulePriority(event)),
    );
    this.componentRef.instance.onRuleToggle.subscribe((event) =>
      this.store.dispatch(AutomationRulesActions.ruleToggle(event)),
    );
    this.componentRef.instance.onEditRule.subscribe((event) =>
      this.store.dispatch(AutomationRulesActions.openEditWorkflowDialog(event)),
    );
    this.componentRef.instance.onDuplicateRule.subscribe((event) =>
      this.store.dispatch(AutomationRulesActions.ruleDuplicate(event)),
    );
    this.componentRef.instance.onDeleteRule.subscribe((event) =>
      this.store.dispatch(AutomationRulesActions.ruleDelete(event)),
    );
    this.isLoadingSubscription$ = this.isLoading$.subscribe((isLoading) => {
      this.setLoader(isLoading);
    });
    this.templateDataSubscription$ = this.templateData$.subscribe(
      (templateData?: AutomationRulesReducer.TemplateData) => {
        if (templateData) {
          this.templateData = {
            parameters: templateData.parameters,
            template: templateData['ng-template'],
            notVerifiedName: templateData.notVerifiedName,
          };
        }
      },
    );
    this.rulesDataSubscription$ = this.rulesData$.subscribe((rulesData: Array<Rule>) => {
      if (rulesData && this.templateData) {
        this.activeRulesCount = rulesData.filter((rule) => rule.enabled).length;
        this.setRulesListComponentData(rulesData);
      }
    });
  }

  ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
    this.isLoadingSubscription$?.unsubscribe();
    this.templateDataSubscription$?.unsubscribe();
    this.rulesDataSubscription$?.unsubscribe();
  }

  initDialogData() {
    if (!this.data) {
      return;
    }
    this.step = this.data.step;

    this._shopService.shop$.pipe(first()).subscribe((shop) => {
      this.isAdminUser = this.userService.isAdmin();
      this.disableAddRule = !this.isAdminUser && shop.isCheckoutOnly;
    });
  }

  createComponent() {
    if (!this.componentRef) {
      this.componentRef = this.vcRef.createComponent(RulesListComponent);
    }
  }

  prepareRulesListData(data): Array<RuleItem> {
    if (!data || !data.length) {
      return [];
    }

    return data.map((rule) => {
      const andWhenValues: any[][] = [[]];
      const conditionNames: string[][] = [[]];
      const conditionTexts: string[][] = [[]];

      for (let j = 0; j < rule.params.orWhen.length; j++) {
        for (let i = 0; i < rule.params.orWhen[j].andWhen.length; i++) {
          const andWhenName: string = Object.keys(rule.params.orWhen[j].andWhen[i])?.pop();
          const andWhenValue: any = Object.values(rule.params.orWhen[j].andWhen[i])?.pop();
          if (!andWhenValues[j]) {
            andWhenValues[j] = [];
          }
          andWhenValues[j].push(andWhenValue);
          const valueToCheck = andWhenValue.values ? andWhenValue.values : andWhenValue;
          const condition = this.templateData.parameters.conditions[0].values.find(
            findConditionByNameAndValue.bind(null, andWhenName, valueToCheck),
          );
          if (!conditionNames[j]) {
            conditionNames[j] = [];
          }
          if (!conditionTexts[j]) {
            conditionTexts[j] = [];
          }
          condition?.name ? conditionNames[j].push(condition.name) : null;
          condition?.text ? conditionTexts[j].push(condition.text) : null;
        }
      }
      const andThenName: string = Object.keys(rule.params.andThen[0])[0];
      const andThenValue: any = Object.values(rule.params.andThen[0])[0];
      const action = this.templateData.parameters.actions[0].values?.find(
        findActionByNameAndValue.bind(null, andThenName, andThenValue),
      );
      return {
        id: rule.id,
        enabled: rule.enabled,
        conditionNames: conditionNames?.length ? conditionNames : [''],
        conditionText: conditionTexts?.length ? conditionTexts : [''],
        conditionValues: andWhenValues,
        actionName: action ? action.name : '',
        actionText: action ? action.text : '',
        actionValue: andThenValue,
        updatedAt: rule.updatedAt,
        params: rule.params,
        internalName: rule.internalName,
      };
    });
  }

  setRulesListComponentData(data) {
    this.createComponent();
    this.setLoader(false);
    this.componentRef.instance.items = this.prepareRulesListData(data);

    this.componentRef.instance.notVerifiedName = this.templateData.notVerifiedName;
    this.componentRef.instance.parameters = this.templateData.parameters;
  }

  setLoader(enabled) {
    this.createComponent();
    this.componentRef.instance.showLoader = enabled;
    this.isLoading = enabled;
  }

  addWorkflow(): void {
    this.store.dispatch(AutomationRulesActions.openAddWorkflowDialog({ step: this.step }));
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onAutomationRulesNavigationClick(changedStep: Step): void {
    this.onClose();
    this.store.dispatch(
      AutomationRulesActions.openAutomationRulesListDialog({
        step: changedStep,
      }),
    );
  }
}
